import { DateTime } from 'luxon';
import { TABLE_HEADINGS, NEW_GROUP_ID } from 'utils/constants';
import { Region, State } from 'utils/constants/location';
import type {
  Group,
  GroupRaw,
  District,
  VerticalRaw,
  GroupRawResponse,
  SortedProject,
  TStartDate,
  EmailFrequency,
} from 'types';

type Notification = {
  is_notification_enabled?: boolean;
};

const rawDistrictsToNumbers = (districts: District[]) => {
  return districts.map((district) => district.id);
};

const rawVerticalsToNumbers = (verticals: VerticalRaw[]) => {
  return verticals.map((vertical) => vertical.id);
};

export const groupRawToGroup = ({
  id,
  name,
  type,
  companies,
  verticals,
  verticals_exclusions,
  districts,
  zone_exclusions,
  triggers,
  past_project_stages,
  exclude_past_project_stages,
  min_project_cost,
  max_project_cost,
  project_cost_include_unknown,
  email_frequency,
  status,
  keywords,
  startDate,
  endDate,
  broker_data_available,
  real_estate_data_available,
  document_url_available,
  is_notification_enabled,
  exclusions,
  work_types,
  work_types_exclusions,
  project_size_min,
  project_size_max,
  project_size_include_unknown,
  parcel_size_min,
  parcel_size_max,
  parcel_size_include_unknown,
  company_keywords,
  company_keywords_exclusions,
  mercator_metropolitan_id,
  mercator_metropolitan_ids,
  state_code,
  city_ids,
  county_codes,
  company_stakeholder_types,
  company_stakeholder_types_exclusions,
  polygon_bounds,
}: GroupRawResponse): Group => ({
  id: id ?? '',
  type,
  name,
  companies: companies.map((company) => company.id),
  districts: rawDistrictsToNumbers(districts),
  zoneExclusions: rawDistrictsToNumbers(zone_exclusions),
  verticals: rawVerticalsToNumbers(verticals),
  verticalsExclusions: rawVerticalsToNumbers(verticals_exclusions),
  mercatorMetropolitanId: mercator_metropolitan_id,
  mercator_metropolitan_ids,
  state_code,
  cityIds: city_ids,
  county_codes: county_codes || [],
  notifications: {
    enabled: is_notification_enabled ?? true,
  },
  email_frequency: email_frequency ?? ('weekly' as EmailFrequency),
  triggers: triggers.map(({ city_id: cityId, document_types }) => ({
    cityId,
    documentTypes: document_types.map(
      ({ document_type_id, categories, document_statuses: statuses }) => ({
        id: document_type_id,
        categories,
        statuses,
      }),
    ),
  })),
  past_project_stage: past_project_stages
    ? past_project_stages.map(({ city_id: cityId, document_types }) => ({
        cityId,
        documentTypes: document_types.map(
          ({ document_type_id, categories, document_statuses: statuses }) => ({
            id: document_type_id,
            categories,
            statuses,
          }),
        ),
      }))
    : [],
  exclude_past_project_stage: exclude_past_project_stages
    ? exclude_past_project_stages.map(({ city_id: cityId, document_types }) => ({
        cityId,
        documentTypes: document_types.map(
          ({ document_type_id, categories, document_statuses: statuses }) => ({
            id: document_type_id,
            categories,
            statuses,
          }),
        ),
      }))
    : [],
  status,
  cost: {
    min: min_project_cost,
    max: max_project_cost,
  },
  project_cost_include_unknown,
  keywords,
  startDate: startDate || '',
  endDate,
  realEstate: real_estate_data_available,
  brokerData: broker_data_available,
  documentUrl: document_url_available,
  exclusions,
  workTypes: work_types,
  workTypesExclusions: work_types_exclusions,
  project_size_min,
  project_size_max,
  project_size_include_unknown,
  parcel_size_min,
  parcel_size_max,
  parcel_size_include_unknown,
  companyKeywords: company_keywords,
  companyKeywordsExclusions: company_keywords_exclusions,
  company_stakeholder_types,
  company_stakeholder_types_exclusions,
  polygon_bounds,
});

/* Transforms group settings from form to expected data format for backend
 * requests.
 */
export const groupToGroupRaw = ({
  id,
  type,
  name,
  notifications,
  email_frequency,
  triggers,
  past_project_stage,
  exclude_past_project_stage,
  districts,
  companies,
  verticals,
  verticalsExclusions,
  cost,
  project_cost_include_unknown,
  keywords,
  status,
  startDate,
  endDate,
  realEstate,
  brokerData,
  documentUrl,
  exclusions,
  zoneExclusions,
  workTypes,
  workTypesExclusions,
  project_size_min,
  project_size_max,
  project_size_include_unknown,
  parcel_size_min,
  parcel_size_max,
  parcel_size_include_unknown,
  companyKeywords,
  companyKeywordsExclusions,
  mercatorMetropolitanId,
  mercator_metropolitan_ids,
  state_code,
  cityIds,
  county_codes,
  company_stakeholder_types,
  company_stakeholder_types_exclusions,
  polygon_bounds,
}: Group): GroupRaw & Notification => ({
  id: id === NEW_GROUP_ID ? undefined : Number.parseInt(`${id}`, 10),
  type: type!,
  name: name ?? '',
  emails: [],
  email_frequency: email_frequency ?? ('weekly' as EmailFrequency),
  is_notification_enabled: notifications?.enabled ?? true,
  min_project_cost: cost.min ?? 0,
  max_project_cost: cost.max ?? 0,
  project_cost_include_unknown,
  districts,
  zone_exclusions: zoneExclusions,
  companies,
  verticals,
  mercator_metropolitan_id: mercatorMetropolitanId || Region.US_AUSTIN,
  mercator_metropolitan_ids,
  state_code: state_code || State.TX,
  city_ids: cityIds,
  county_codes,
  verticals_exclusions: verticalsExclusions,
  keywords,
  triggers: triggers.map(({ cityId, documentTypes }) => ({
    city_id: cityId,
    document_types: documentTypes
      .map(({ categories, statuses, id: document_type_id }) => ({
        document_type_id,
        categories,
        document_statuses: statuses,
      }))
      .filter(
        ({ document_statuses, categories }) =>
          document_statuses.length !== 0 || categories.length !== 0,
      ),
  })),
  past_project_stages: past_project_stage.map(({ cityId, documentTypes }) => ({
    city_id: cityId,
    document_types: documentTypes
      .map(({ categories, statuses, id: document_type_id }) => ({
        document_type_id,
        categories,
        document_statuses: statuses,
      }))
      .filter(
        ({ document_statuses, categories }) =>
          document_statuses.length !== 0 || categories.length !== 0,
      ),
  })),
  exclude_past_project_stages: exclude_past_project_stage.map(
    ({ cityId, documentTypes }) => ({
      city_id: cityId,
      document_types: documentTypes
        .map(({ categories, statuses, id: document_type_id }) => ({
          document_type_id,
          categories,
          document_statuses: statuses,
        }))
        .filter(
          ({ document_statuses, categories }) =>
            document_statuses.length !== 0 || categories.length !== 0,
        ),
    }),
  ),
  status,
  startDate,
  endDate,
  real_estate_data_available: realEstate,
  broker_data_available: brokerData,
  document_url_available: documentUrl,
  exclusions,
  work_types: workTypes,
  work_types_exclusions: workTypesExclusions,
  project_size_min,
  project_size_max,
  project_size_include_unknown,
  parcel_size_min,
  parcel_size_max,
  parcel_size_include_unknown,
  company_keywords: companyKeywords,
  company_keywords_exclusions: companyKeywordsExclusions,
  company_stakeholder_types,
  company_stakeholder_types_exclusions,
  polygon_bounds,
});

export const triggersToRaw = (triggers: Group['triggers']) => {
  return triggers.map(({ cityId, documentTypes }) => ({
    city_id: cityId,
    document_types: documentTypes.map(
      ({ categories, statuses, id: document_type_id }) => ({
        document_type_id,
        categories,
        document_statuses: statuses,
      }),
    ),
  }));
};

export const pastStagesToRaw = (triggers: Group['past_project_stage']) => {
  return triggers.map(({ cityId, documentTypes }) => ({
    city_id: cityId,
    document_types: documentTypes.map(
      ({ categories, statuses, id: document_type_id }) => ({
        document_type_id,
        categories,
        document_statuses: statuses,
      }),
    ),
  }));
};

export const excludePastStagesToRaw = (triggers: Group['exclude_past_project_stage']) => {
  return triggers.map(({ cityId, documentTypes }) => ({
    city_id: cityId,
    document_types: documentTypes.map(
      ({ categories, statuses, id: document_type_id }) => ({
        document_type_id,
        categories,
        document_statuses: statuses,
      }),
    ),
  }));
};

export const formatFilterStartDate = (startDate?: TStartDate | undefined): string => {
  if (!startDate || startDate === 'anytime') {
    return '';
  }

  const [numberToSubtract, typeToSubtract] = startDate.split('_');

  return DateTime.now()
    .minus({ [typeToSubtract]: numberToSubtract })
    .toFormat('dd-LL-yyyy');
};

/* eslint-disable */
export const sortProjectData = (
  data: SortedProject[],
  heading: string,
  setSortArray: (data: string[]) => void,
  reverse?: boolean,
  clientSort: boolean = false,
) => {
  const sortChange = (dataObj: SortedProject[], option: keyof SortedProject) => {
    if (!clientSort) {
      if (!reverse) {
        setSortArray([`${option}`]);
      } else {
        setSortArray([`-${option}`]);
      }
      return;
    }
  };

  switch (heading) {
    case TABLE_HEADINGS.CITY:
      // If clientSort is true, we are sorting on the client side.
      // Otherwise setSortArray to appropriate key for server side sort
      return sortChange(data, 'city');
    case TABLE_HEADINGS.PROVINCE:
      return sortChange(data, 'state');
    case TABLE_HEADINGS.SECTORS:
      if (!clientSort) {
        if (!reverse) {
          setSortArray(['verticals']);
        } else {
          setSortArray(['-verticals']);
        }
        return;
      } else {
        return data.sort((a, b) => {
          if (a.vertical < b.vertical) {
            if (reverse) return 1;
            return -1;
          }
          if (a.vertical > b.vertical) {
            if (reverse) return -1;
            return 1;
          }
          return 0;
        });
      }
    case TABLE_HEADINGS.EVENT_TRIGGERS:
      if (!clientSort) {
        if (!reverse) {
          setSortArray(['document_type']);
        } else {
          setSortArray(['-document_type']);
        }
        return;
      } else {
        return data.sort((a, b) => {
          if (
            (a.latest_document ? a.latest_document.type : a.latest_activity.type) <
            (b.latest_document ? b.latest_document.type : b.latest_activity.type)
          ) {
            if (reverse) return 1;
            return -1;
          }
          if (
            (a.latest_document ? a.latest_document.type : a.latest_activity.type) >
            (b.latest_document ? b.latest_document.type : b.latest_activity.type)
          ) {
            if (reverse) return -1;
            return 1;
          }
          return 0;
        });
      }
    case TABLE_HEADINGS.STATUS:
      return sortChange(data, 'status');
    case TABLE_HEADINGS.COST:
      if (!clientSort) {
        if (!reverse) {
          setSortArray(['cost']);
        } else {
          setSortArray(['-cost']);
        }
        return;
      } else {
        return data.sort((a, b) => {
          // sort cost from lowest to highest
          // check if cost is null
          if (a.cost === null || b.cost === null) {
            return -1;
          }
          if (a.cost < b.cost) {
            if (reverse) return 1;
            return -1;
          }
          if (a.cost > b.cost) {
            if (reverse) return -1;
            return 1;
          }
          return 0;
        });
      }
    case TABLE_HEADINGS.LAST_UPDATE:
      if (!clientSort) {
        if (!reverse) {
          setSortArray(['latest_activity_date']);
        } else {
          setSortArray(['-latest_activity_date']);
        }
        return;
      } else {
        return data.sort((a, b) => {
          // sort date from latest to oldest date strings
          const dateA = new Date(
            (a.latest_activity_date ? a.latest_activity_date : a.latest_activity.date) ??
            '',
          );
          const dateB = new Date(
            (b.latest_activity_date ? b.latest_activity_date : b.latest_activity.date) ??
            '',
          );

          if (dateA < dateB) {
            if (reverse) return 1;
            return -1;
          }
          if (dateA > dateB) {
            if (reverse) return -1;
            return 1;
          }
          return 0;
        });
      }
    case TABLE_HEADINGS.DATE_CREATED:
      if (!reverse) {
        setSortArray(['startdate']);
      } else {
        setSortArray(['-startdate']);
      }
      return;

    case TABLE_HEADINGS.LATEST_COMPANY:
      if (!clientSort) {
        if (!reverse) {
          setSortArray(['company']);
        } else {
          setSortArray(['-company']);
        }
        return;
      } else {
        return data.sort((a, b) => {
          if (a.latest_company?.name && b.latest_company?.name) {
            if (a?.latest_company?.name < b?.latest_company?.name) {
              if (reverse) return 1;
              return -1;
            }
            if (a?.latest_company?.name > b?.latest_company?.name) {
              if (reverse) return -1;
              return 1;
            }
          } else if (a.latest_company?.name && !b.latest_company?.name) {
            if (reverse) return 1;
            return -1;
          } else if (!a.latest_company?.name && b.latest_company?.name) {
            if (reverse) return -1;
            return 1;
          }
          return 0;
        });
      }
    case TABLE_HEADINGS.CATEGORY:
      if (!clientSort) {
        if (!reverse) {
          setSortArray(['category']);
        } else {
          setSortArray(['-category']);
        }
        return;
      } else {
        return data.sort((a, b) => {
          if (a.category < b.category) {
            if (reverse) return 1;
            return -1;
          }
          if (a.category > b.category) {
            if (reverse) return -1;
            return 1;
          }
          return 0;
        });
      }
  }
};
/* eslint-enable */

export const textFromCronString = (cronExpression: string | undefined) => {
  if (cronExpression) {
    if (cronExpression?.slice(-5) === '* * *') return 'Daily';
    if (cronExpression.slice(-1) !== '*') return 'Weekly';
    if (cronExpression?.slice(-3) === '* *') return 'Monthly';
  }

  return '';
};
